import { VZButton } from 'components/Button/button.styles';
import { ValidationErrors } from 'final-form';
import { IFormError } from 'models';
import React, { PropsWithRef, RefObject } from 'react';
import { Form as FinalForm, FormRenderProps, FormSpy, FormSpyRenderProps } from 'react-final-form';
import { ContentUtil, globalContentUtil } from 'utils/content';
import { scrollTo } from 'utils/scroll';
import { v4 as uuid } from 'uuid';

import * as S from './form.styles';
import { validators } from './validate';

interface FormPropsBase {
  onSubmit: (values: unknown) => Promise<void>;
  buttonText?: string | React.ReactNode;
  buttonTextCancel?: string | React.ReactNode;
  buttonTextConfirm?: string;
  hasConfirmation?: boolean;
  confirmationMessage?: string;
  confirmationMessageOk?: string;
  confirmationMessageCancel?: string;
  id?: string;
  afterSuccessModelClose?: () => void;
  successMessage?: string;
  successButtonText?: string;
  successButtonHref?: string;
  failedMessage?: string;
  initialValues?: object;
  dotTheme?: 'primary' | 'secondary' | 'tertiary';
  render: (
    api: FormRenderProps<unknown>,
    formvalidators: typeof validators,
    itemValidation: (key: string, translateKey?: string) => string | undefined,
    comparePasswords: (
      password: string,
      passwordRepeat: string,
      formApi: FormRenderProps<unknown>
    ) => string | undefined
  ) => React.ReactNode;
  isSuccess?: boolean;
  isFailed?: boolean;
  isLoading?: boolean;
  itemValidationErrors?: IFormError[];
  onUpdate?: (values: unknown) => void;
  modalButtonsStyled?: boolean;
  hideSubmitButton?: boolean;
  hideCancelButton?: boolean;
  onCancel?: () => void;
  contentUtil?: ContentUtil;
  testid?: string;
  grid?: boolean;
  scrollToRef?: RefObject<unknown>;
  skipScrollToRef?: boolean;
  validate?: (
    values: string | number | boolean | object | undefined
  ) => ValidationErrors | Promise<ValidationErrors> | undefined;
}

type FormProps = FormPropsBase;

interface FormState {
  values: string | number | boolean | object | undefined;
  closeModal?: boolean;
  passwordsNotEqual: boolean;
}

export class Form extends React.Component<FormProps, FormState> {
  state: FormState = {
    values: this.props.initialValues,
    passwordsNotEqual: false,
  };

  private id: string;

  private contentUtil: ContentUtil;

  public ref: PropsWithRef<RefObject<HTMLFormElement>>;

  constructor(props: FormProps) {
    super(props);
    this.id = props.id || uuid();
    this.ref = React.createRef();
    this.contentUtil = props.contentUtil || globalContentUtil;
  }

  getErrorTranslation(key: string) {
    return this.contentUtil.hasTranslation(`error.validation.${key}`) && key;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSubmit = async (e: React.FormEvent<HTMLFormElement>, formApi: any) => {
    // Check if we need to show a confirmation message before we submit the form
    if (!this.props.hasConfirmation) {
      let coords = 0;
      const ref = (this.props.scrollToRef && this.props.scrollToRef.current) || (this.ref && this.ref.current);
      if (ref) {
        coords = (ref as Element).getBoundingClientRect().top;
      }
      await formApi.handleSubmit(e);

      // trigger tracking event
      this.id && this.ddmTrigger(this.props.isFailed ? 'fail' : 'success', this.id);

      !this.props.skipScrollToRef && scrollTo(coords);
    } else {
      e.preventDefault();
    }
  };

  /**
   * Add an possible 3party error message into the field based on the given errors
   * @param key :same name as the backend error response item_key
   * @param translateKey :default is the backend error repsonse error_code, you can override this.
   * @returns Message | undefined
   * @private
   */
  validateItem = (key: string, translateKey?: string) => {
    const { itemValidationErrors } = this.props;
    const foundError = itemValidationErrors && itemValidationErrors.find((err: IFormError) => err.field === key);
    if (foundError) {
      const errorKey =
        translateKey ||
        this.getErrorTranslation(foundError.code.toLowerCase()) ||
        this.getErrorTranslation(foundError.field.toLowerCase());

      return this.contentUtil.translateToString(`error.validation.${errorKey}`);
    }
    return undefined;
  };

  comparePasswords = (
    password: string,
    passwordRepeat: string,
    formApi: FormRenderProps<unknown, Partial<unknown>>
  ) => {
    const { modified } = formApi;
    const values = formApi.values as { [key: string]: unknown };

    if (modified?.[password] && modified?.[passwordRepeat] && values[password] !== values[passwordRepeat]) {
      // only update the state if needed
      if (!this.state.passwordsNotEqual) {
        this.setState({ passwordsNotEqual: true });
      }
      return this.contentUtil.translateToString(`error.validation.passwordsMatch`);
    }

    // only update the state if needed
    if (this.state.passwordsNotEqual) {
      this.setState({ passwordsNotEqual: false });
    }

    return undefined;
  };

  /**
   * Send response for tracking purposes
   */
  ddmTrigger = (response: 'success' | 'fail', formName: string, err?: { [key: string]: unknown }) => {
    let errorArray;

    if (err && err.errorMessages) {
      errorArray = Object.keys(err.errorMessages);
    }

    if (window._ddm) {
      window._ddm.trigger('form.submit', {
        data: {
          result: response,
          name: formName,
          error: errorArray || undefined,
        },
      });
    }
  };

  onFormUpdate = (formSpy: FormSpyRenderProps<object>) => {
    if (this.props.onUpdate) {
      // check if the values have actually updated, as formspy also triggers on rerenders
      if (JSON.stringify(formSpy.values) !== JSON.stringify(this.state.values)) {
        this.setState({ values: formSpy.values }, () => {
          if (this.props.onUpdate) {
            this.props.onUpdate(formSpy.values);
          }
        });
      }
    }
    return null;
  };

  render() {
    const {
      buttonText,
      buttonTextCancel,
      failedMessage,
      hideSubmitButton,
      hideCancelButton,
      modalButtonsStyled,
      initialValues,
      isFailed,
      isLoading,
      isSuccess,
      onSubmit,
      onUpdate,
      render,
      onCancel,
      testid,
      validate,
      grid = true,
      dotTheme,
      ...rest
    } = this.props;

    return (
      <React.Fragment>
        {isFailed && failedMessage && <S.FormError>{failedMessage}</S.FormError>}
        <FinalForm
          onSubmit={onSubmit}
          initialValues={initialValues || {}}
          validate={validate}
          render={(formApi) => {
            return (
              <S.Form
                ref={this.ref}
                grid={grid}
                id={this.id}
                data-testid={testid}
                onSubmit={(e) => this.handleSubmit(e, formApi)}
                noValidate
                {...rest}>
                {render(formApi, validators, this.validateItem, this.comparePasswords)}
                <S.ButtonContainer className={`form-button ${modalButtonsStyled ? 'form-buttons-modal' : ''}`}>
                  <VZButton
                    type="submit"
                    testid="form-submit-button"
                    className={`${hideSubmitButton ? 'hidden' : ''}`}
                    dotTheme={dotTheme || 'secondary'}>
                    {!(isSuccess || isLoading) &&
                      (buttonText || globalContentUtil.translate('global.forms.button.submit'))}
                    {isSuccess && !isLoading && globalContentUtil.translate('global.forms.button.success')}
                    {isLoading && (
                      <React.Fragment>{globalContentUtil.translate('global.forms.button.loading')}</React.Fragment>
                    )}
                  </VZButton>
                  <VZButton
                    variant="outlined"
                    dotTheme="secondary"
                    className={`btn-cancel ${hideCancelButton ? 'hidden' : ''}`}
                    testid="form-cancel-button"
                    onClick={() => onCancel && onCancel()}>
                    {buttonTextCancel || globalContentUtil.translate('global.forms.button.cancel')}
                  </VZButton>
                </S.ButtonContainer>
                {onUpdate && <FormSpy subscription={{ values: true, dirty: true }} onChange={this.onFormUpdate} />}
              </S.Form>
            );
          }}
        />
      </React.Fragment>
    );
  }
}
